import { FRAME_MS, drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { ActResult, CasinoGame, TableEnv } from './types';

/**
 * Plinko: a ball drops through 8 rows of pegs, bouncing left or right at
 * each, and lands in one of 9 slots. The middle is likely and pays little;
 * the edges are rare and pay big. Three risk levels change how steep that
 * is. Each table is scaled to return exactly TARGET_RTP (tables.test.ts).
 *
 * The path is drawn when you drop; the ball then falls one row every
 * ROW_MS on the lens (driven by the real-time tick, like Horse Racing) and
 * the bet settles when it lands. You're locked in until then.
 */
export const ROWS = 8;
export const ROW_MS = 300;
/** Board width in characters: slots sit at 0, 2, ... 16, pegs between. */
const WIDTH = ROWS * 2 + 1;
export type PlinkoRisk = 'low' | 'medium' | 'high';
const RISKS: PlinkoRisk[] = ['low', 'medium', 'high'];

/** P(slot k) = C(8, k) / 256. */
export const SLOT_ODDS = [1, 8, 28, 56, 70, 56, 28, 8, 1].map((n) => n / 256);

const SHAPES: Record<PlinkoRisk, number[]> = {
  low: [5.6, 2.1, 1.1, 1, 0.5, 1, 1.1, 2.1, 5.6],
  medium: [13, 3, 1.3, 0.7, 0.4, 0.7, 1.3, 3, 13],
  high: [29, 4, 1.5, 0.3, 0.2, 0.3, 1.5, 4, 29],
};

export const TARGET_RTP = 0.96;

export function plinkoTable(risk: PlinkoRisk): number[] {
  const shape = SHAPES[risk];
  const rtp = shape.reduce((s, m, i) => s + m * SLOT_ODDS[i]!, 0);
  return shape.map((m) => (m * TARGET_RTP) / rtp);
}

/** A risk level's results (for the ★'s second-chance rate). */
export function plinkoOutcomes(risk: PlinkoRisk): Outcome[] {
  return plinkoTable(risk).map((m, i) => [SLOT_ODDS[i]!, m] as const);
}

export interface PlinkoState {
  phase: 'idle' | 'dropping' | 'done';
  risk: PlinkoRisk;
  /** Left/right bounces of the ball (0 = left). */
  path: number[];
  /** Landing slot once settled. */
  slot: number | null;
  lastStake: number;
  returned: number;
  lastRisk: PlinkoRisk;
  /** Edge-slot landings in a row on High (the jackpot needs two). */
  edgeStreak: number;
  startedAt: number;
  /** Rows the ball has fallen through (0-ROWS); only changes redraw. */
  shown: number;
  /** The ★'s second chance re-dropped a losing ball. */
  lucky: boolean;
}

/**
 * The peg board with the ball after `fallen` rows: row k has k + 1 pegs,
 * and after k bounces with b of them to the right the ball sits on peg b of
 * row k. Once past the last row it lies in its slot on the bottom line.
 */
export function plinkoBoard(path: readonly number[], fallen: number): string[] {
  const right = path.slice(0, fallen).reduce((s, d) => s + d, 0);
  const lines: string[] = [];
  for (let k = 0; k < ROWS; k++) {
    const row = Array<string>(WIDTH).fill(' ');
    for (let j = 0; j <= k; j++) row[ROWS - k + 2 * j] = '.';
    if (k === fallen) row[ROWS - k + 2 * right] = 'O';
    lines.push(row.join('').trimEnd());
  }
  const slots = Array<string>(WIDTH).fill('_');
  if (fallen >= ROWS) slots[2 * right] = 'O';
  lines.push(slots.join(''));
  return lines;
}

/** Casino frames of a ball dropping along `path` (the losing first ball
 *  before a ★ second chance), in the same 6-line window as the real drop. */
function dropFrames(path: readonly number[]): string[] {
  return Array.from({ length: ROWS + 1 }, (_, fallen) => {
    const board = plinkoBoard(path, fallen);
    const top = Math.max(0, Math.min(fallen - 2, board.length - 6));
    return board.slice(top, top + 6).join('\n');
  });
}

function settle(state: PlinkoState, env: TableEnv): ActResult<PlinkoState> {
  const slot = state.path.reduce((s, d) => s + d, 0);
  const returned = Math.round(state.lastStake * plinkoTable(state.lastRisk)[slot]!);
  const edge = slot === 0 || slot === ROWS;
  const edgeStreak = state.lastRisk === 'high' && edge ? state.edgeStreak + 1 : 0;
  return {
    state: { ...state, phase: 'done', shown: ROWS, slot, returned, edgeStreak },
    bankroll: env.bankroll + returned,
    settled: { wagered: state.lastStake, returned },
    jackpot: edgeStreak >= 2,
  };
}

const riskName = (r: PlinkoRisk) => r[0]!.toUpperCase() + r.slice(1);

export const plinko: CasinoGame<PlinkoState> = {
  id: 'plinko',
  noDealer: true,
  usesLuck: true,
  name: 'Plinko',
  blurb: 'Drop a ball through 8 rows of pegs. Edges pay big.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a losing ball sometimes drops again.',
  jackpotRule: 'Hit an edge slot twice in a row on High.',
  lockedHint: '(the ball is falling)',
  create: () => ({ phase: 'idle', risk: 'medium', path: [], slot: null, lastStake: 0, returned: 0, lastRisk: 'medium', edgeStreak: 0, startedAt: 0, shown: 0, lucky: false }),
  locked: (state) => state.phase === 'dropping',
  options(state) {
    if (state.phase === 'dropping') return [{ id: 'watch', label: 'Dropping...' }];
    return [
      { id: 'drop', label: state.phase === 'done' ? 'Drop again' : 'Drop' },
      { id: 'risk', label: `Risk: ${riskName(state.risk)}` },
    ];
  },
  describe(state, env, highlighted) {
    if (state.phase === 'dropping') {
      // The pane fits ~8 lines with the locked hint: show 6 board lines,
      // scrolling down with the ball.
      const board = plinkoBoard(state.path, state.shown);
      const top = Math.max(0, Math.min(state.shown - 2, board.length - 6));
      return board.slice(top, top + 6).join('\n');
    }
    if (highlighted === 'risk') {
      const table = plinkoTable(state.risk);
      return `Edge to middle:\n${table.slice(0, 5).map((m) => `x${m.toFixed(1)}`).join(' ')}\n\nTap to change.`;
    }
    if (state.slot === null) return 'Tap Drop. Middle slots are likely and pay little; the edges are rare.';
    const table = plinkoTable(state.lastRisk);
    const net = state.returned - state.lastStake;
    // Result first, then the bottom of the board with the ball in its slot.
    const result = `${state.lucky ? '★ ' : ''}x${table[state.slot]!.toFixed(2)}  ${net > 0 ? `+$${net}` : net === 0 ? 'Even' : `-$${-net}`}`;
    return [result, ...plinkoBoard(state.path, ROWS).slice(-5)].join('\n');
  },
  act(state, optionId, env) {
    if (state.phase === 'dropping') return { state, bankroll: env.bankroll };
    if (optionId === 'risk') return { state: { ...state, risk: RISKS[(RISKS.indexOf(state.risk) + 1) % RISKS.length]! }, bankroll: env.bankroll };
    if (optionId !== 'drop') return { state, bankroll: env.bankroll };
    // The ★'s luck may re-drop a ball that would lose (only the re-drop is shown).
    const table = plinkoTable(state.risk);
    const drop = () => Array.from({ length: ROWS }, () => env.rng.int(2));
    const q = secondChanceRate(plinkoOutcomes(state.risk), luckOf(env));
    const { outcome: path, lucky, first } = drawWithLuck(env.rng, drop, (p) => table[p.reduce((s, d) => s + d, 0)]! < 1, q);
    // A ★ second chance: the losing ball drops first, then another one (its
    // own animation starts after those frames).
    const frames = first === undefined ? [] : [...dropFrames(first), secondChanceFrame(plinkoBoard(first, ROWS).slice(-4).join('\n'))];
    return {
      state: { ...state, phase: 'dropping', path, lucky, slot: null, lastStake: env.bet, returned: 0, lastRisk: state.risk, startedAt: env.now + frames.length * FRAME_MS, shown: 0 },
      bankroll: env.bankroll - env.bet,
      frames,
    };
  },
  tick(state, env) {
    if (state.phase !== 'dropping') return null;
    const shown = Math.max(0, Math.floor((env.now - state.startedAt) / ROW_MS));
    if (shown > ROWS) return settle(state, env);
    return shown === state.shown ? null : { state: { ...state, shown }, bankroll: env.bankroll };
  },
};
